import { useQuery } from '@tanstack/react-query';
import {
  ADMIN_RECEIVABLES,
  AdminSpinner,
  ArrowRed,
  AreaTooltip,
  Box,
  Disclaimer,
  Div,
  GroupByType,
  H2,
  H3,
  H5,
  Icon,
  NumberType,
  P,
  ReceivablesCollection,
  ReceivablesCollectionKind,
  ReceivablesOverviewSlice,
  formatAmount,
  getReceivablesOverview,
  getTimeZone,
  lightTheme,
  queryKeys,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  CollectionBar,
  CollectionLegend,
  CollectionPart,
  Dot,
  KindRow,
  LegendItem,
  PieLayout,
  ServiceColumns,
  ServiceHead,
  SmallDot,
} from './ClientOutstandingBalance.styled';
import { TabKey, TabToggle, TrendBody } from './FinanceTabbedCard';

/**
 * One color per kind of debt. The backend's keys are a fixed set, so a slice
 * keeps its color whatever else is owed: vDrive in blues and grays, SaaS in
 * reds and yellows. A function, not a map: `lightTheme` is a barrel value and
 * reading it at module top level breaks eagerly loaded files.
 */
const sliceColor = (key: string) => {
  switch (key) {
    case 'vDrive:reserve':
      return lightTheme.darkBlue;
    case 'vDrive:failedAch':
      return lightTheme.blue;
    case 'vDrive:achUnavailable':
      return lightTheme.darkGray;
    case 'vDrive:vDriveCardCharge':
      return lightTheme.green;
    case 'vDrive:backupCardCharge':
      return lightTheme.lightGray;
    case 'vDrive:legacyDebt':
      return lightTheme.yellow;
    case 'vDrive:manual':
      // The palette runs out of solid hues here; manual items are rare.
      return `${lightTheme.blue}80`;
    case 'saas:failedAch':
      return lightTheme.primary;
    case 'saas:achUnavailable':
      return lightTheme.orange;
    case 'saas:legacyDebt':
      return lightTheme.darkYellow;
    case 'saas:manual':
      return lightTheme.chipYellow;
    default:
      return lightTheme.lightGray;
  }
};

const SERVICES = [
  { service: 'vDrive', label: 'vDrive' },
  { service: 'saas', label: 'SaaS' },
] as const;

const usd = (cents: number) => formatAmount(cents, NumberType.USD);

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** A kind's history, by `breakdown` key; the vDrive reserve has none. */
type CollectionByKey = Map<string, ReceivablesCollectionKind>;

/** "collected $300.19 of $28,553.96 · written off $50.00" for one kind. */
const collectedLine = (kind: ReceivablesCollectionKind) =>
  [
    `collected ${usd(kind.collected)} of ${usd(kind.initialDebt)}`,
    kind.writtenOff > 0 && `written off ${usd(kind.writtenOff)}`,
  ]
    .filter(Boolean)
    .join(' · ');

/** Green once something came in; gray while nothing has. */
const collectedColor = (kind: ReceivablesCollectionKind) =>
  kind.collected > 0 ? lightTheme.green : lightTheme.lightGray;

type Props = {
  from: string;
  to: string;
  groupByType: GroupByType;
  /** The selected range in words, for the graph. */
  periodLabel?: string;
};

/**
 * "Client Outstanding Balance": what locations owe vGrubs, as its own
 * section on the Finances tab, the full width of the screen.
 *
 * The pie is everything owed right now, by kind, with the legend split into
 * vDrive and SaaS so every kind fits without a "View all". The graph is the
 * new debt that arose in the selected range; it does not add up to the total,
 * which ignores the range, so it carries its own figure. The arrow opens the
 * Outstanding Balances page, the only way in to it.
 *
 * Compare mode does not apply: there is no compared overview.
 */
export const ClientOutstandingBalance = ({
  from,
  to,
  groupByType,
  periodLabel,
}: Props) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('breakdown');
  const params = {
    from: DateTime.fromISO(from).startOf('day').toISODate() || '',
    to: DateTime.fromISO(to).endOf('day').toISODate() || '',
    timeZone: getTimeZone,
    groupByType,
  };
  const { data: overview, isLoading } = useQuery({
    queryKey: queryKeys.admin.receivables.overview(params),
    queryFn: async () => (await getReceivablesOverview(params)).data.data,
  });

  const breakdown = overview?.breakdown ?? [];
  const collection = overview?.collection;
  const collectionByKey: CollectionByKey = new Map(
    (collection?.byKind ?? []).map((kind) => [kind.key, kind])
  );
  // Kinds paid off or written off in full are no longer in `breakdown`, but
  // what was collected on them still belongs in their column.
  const settled = (collection?.byKind ?? []).filter(
    (kind) =>
      kind.remaining <= 0 && !breakdown.some((slice) => slice.key === kind.key)
  );
  const pending = overview?.pendingBankTotal ?? 0;
  const meta = overview
    ? [
        `Owed now by ${plural(overview.locationsCount, 'location')}`,
        pending > 0 && `${usd(pending)} on its way by bank`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const openReceivables = (
    <Icon
      $pointer
      color={lightTheme.lightGray}
      aria-label="Open Outstanding Balances"
      onClick={() => navigate(ADMIN_RECEIVABLES)}
    >
      <ArrowRed />
    </Icon>
  );

  return (
    <Div>
      <H3 $mb="16">Client Outstanding Balance</H3>
      <Box $relative $flex $column $gap="24px" $p="20px 24px">
        {isLoading && (
          <AdminSpinner small absolute overflowBorderRadius="20px" creme />
        )}

        {/* On mobile the header stacks into a column, so the arrow is pinned
            to the card's top-right corner like the other finance cards; on
            desktop it rides inline with the tabs (below). */}
        <Div
          $mobile
          $absolute
          $flex
          $alignCenter
          style={{ top: 12, right: 16, zIndex: 1 }}
        >
          {openReceivables}
        </Div>

        <Div $flex $between $alignStart $gap="16px" $columnSM>
          <Div>
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {tab === 'graph'
                ? `New debt${periodLabel ? `, ${periodLabel}` : ''}`
                : 'Total owed'}
            </Disclaimer>
            <H2 $m="0" $color={lightTheme.primary}>
              {usd(
                tab === 'graph'
                  ? overview?.newInRange ?? 0
                  : overview?.total ?? 0
              )}
            </H2>
            {tab === 'breakdown' && meta && (
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {meta}
              </Disclaimer>
            )}
            {tab === 'breakdown' &&
              collection &&
              collection.initialDebt > 0 && (
                <CollectionSummary collection={collection} />
              )}
          </Div>
          <Div $flex $alignCenter $gap="8px">
            <TabToggle value={tab} onChange={setTab} />
            <Div $desktop $flex $alignCenter>
              {openReceivables}
            </Div>
          </Div>
        </Div>

        {tab === 'graph' ? (
          <TrendBody
            data={(overview?.series ?? []).map((point) => ({
              key: point.key,
              outstanding: point.value,
            }))}
            seriesKey="outstanding"
            seriesColor={lightTheme.primary}
            tooltipLabel="New debt"
            negateTooltip={false}
            groupByType={groupByType}
            isComparing={false}
          />
        ) : breakdown.length === 0 ? (
          !isLoading && (
            <P $m="0" $center $color={lightTheme.lightGray}>
              Nothing is owed right now.
            </P>
          )
        ) : (
          <PieLayout>
            <BreakdownPie
              slices={breakdown}
              collectionByKey={collectionByKey}
            />
            <ServiceColumns>
              {SERVICES.map(({ service, label }) => {
                const kinds = breakdown.filter(
                  (slice) => slice.service === service
                );
                const settledKinds = settled.filter(
                  (kind) => kind.service === service
                );
                if (kinds.length === 0 && settledKinds.length === 0)
                  return null;
                const subtotal = kinds.reduce(
                  (sum, slice) => sum + slice.amount,
                  0
                );
                return (
                  <div key={service}>
                    <ServiceHead>
                      <H5 $m="0">{label}</H5>
                      <P $m="0" $bold $color={lightTheme.primary}>
                        {usd(subtotal)}
                      </P>
                    </ServiceHead>
                    {kinds.map((slice) => {
                      const kind = collectionByKey.get(slice.key);
                      return (
                        <KindRow key={slice.key}>
                          <Dot $color={sliceColor(slice.key)} />
                          <div>
                            <P $m="0">{slice.label}</P>
                            <Disclaimer $m="0" $color={lightTheme.lightGray}>
                              {plural(slice.locationsCount, 'location')}
                            </Disclaimer>
                            {kind && (
                              <Disclaimer $m="0" $color={collectedColor(kind)}>
                                {collectedLine(kind)}
                              </Disclaimer>
                            )}
                          </div>
                          <Div $flex $column $alignEnd>
                            <P $m="0">{usd(slice.amount)}</P>
                            <Disclaimer $m="0" $color={lightTheme.lightGray}>
                              {slice.percentage.toFixed(2)}%
                            </Disclaimer>
                          </Div>
                        </KindRow>
                      );
                    })}
                    {settledKinds.map((kind) => (
                      <KindRow key={kind.key}>
                        <Dot $color={sliceColor(kind.key)} />
                        <div>
                          <P $m="0" $color={lightTheme.lightGray}>
                            {kind.label}
                          </P>
                          <Disclaimer $m="0" $color={collectedColor(kind)}>
                            {collectedLine(kind)}
                          </Disclaimer>
                        </div>
                        <Div $flex $column $alignEnd>
                          <P $m="0" $color={lightTheme.lightGray}>
                            {usd(0)}
                          </P>
                          <Disclaimer $m="0" $color={lightTheme.lightGray}>
                            Nothing owed
                          </Disclaimer>
                        </Div>
                      </KindRow>
                    ))}
                  </div>
                );
              })}
            </ServiceColumns>
          </PieLayout>
        )}
      </Box>
    </Div>
  );
};

/**
 * Every debt ever recorded as one bar, with what each part is worth under
 * it. The whole bar is `initialDebt`; "Total owed" above stays `total`, which
 * also counts the vDrive reserve.
 */
const CollectionSummary = ({
  collection,
}: {
  collection: ReceivablesCollection;
}) => {
  const share = (cents: number) => (cents / collection.initialDebt) * 100;
  const parts = [
    {
      key: 'collected',
      color: lightTheme.green,
      amount: collection.collected,
      text: `Collected ${usd(collection.collected)} of ${usd(
        collection.initialDebt
      )} (${collection.collectedPercentage.toFixed(2)}%)`,
    },
    {
      key: 'writtenOff',
      color: lightTheme.lightGray,
      amount: collection.writtenOff,
      text: `Written off ${usd(collection.writtenOff)}`,
    },
    {
      key: 'remaining',
      color: lightTheme.primary,
      amount: collection.remaining,
      text: `Still owed ${usd(collection.remaining)}`,
    },
  ];
  return (
    <>
      <CollectionBar
        role="img"
        aria-label={parts.map((part) => part.text).join(', ')}
      >
        {parts.map((part) => (
          <CollectionPart
            key={part.key}
            $color={part.color}
            $width={share(part.amount)}
          />
        ))}
      </CollectionBar>
      <CollectionLegend>
        {parts.map((part) => (
          <LegendItem key={part.key}>
            <SmallDot $color={part.color} />
            <Disclaimer
              $m="0"
              $color={
                part.key === 'collected'
                  ? lightTheme.green
                  : lightTheme.lightGray
              }
            >
              {part.text}
            </Disclaimer>
          </LegendItem>
        ))}
      </CollectionLegend>
    </>
  );
};

/** Every kind as a slice, in the colors the legend uses. */
const BreakdownPie = ({
  slices,
  collectionByKey,
}: {
  slices: ReceivablesOverviewSlice[];
  collectionByKey: CollectionByKey;
}) => (
  <Div $width="240px" $widthSM="100%" $height="240px" $heightSM="200px">
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={slices}
          dataKey="amount"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius="100%"
          labelLine={false}
          animationDuration={1000}
        >
          {slices.map((slice) => (
            <Cell key={slice.key} fill={sliceColor(slice.key)} />
          ))}
        </Pie>
        <Tooltip content={<SliceTooltip collectionByKey={collectionByKey} />} />
      </PieChart>
    </ResponsiveContainer>
  </Div>
);

const SliceTooltip = ({
  active,
  payload,
  collectionByKey,
}: {
  active?: boolean;
  payload?: { payload: ReceivablesOverviewSlice }[];
  collectionByKey: CollectionByKey;
}) => {
  const slice = active ? payload?.[0]?.payload : undefined;
  if (!slice) return null;
  const kind = collectionByKey.get(slice.key);
  return (
    <AreaTooltip>
      <P $m="0" $bold>
        {slice.label}
      </P>
      <P $m="0">
        Owed:{' '}
        <span style={{ color: lightTheme.primary }}>{usd(slice.amount)}</span>
      </P>
      <Disclaimer $m="0">
        {slice.percentage.toFixed(2)}% of total ·{' '}
        {plural(slice.locationsCount, 'location')}
      </Disclaimer>
      {kind && (
        <Disclaimer $m="0" $color={collectedColor(kind)}>
          {collectedLine(kind)}
        </Disclaimer>
      )}
    </AreaTooltip>
  );
};
